/**
 * A tradução de resumos e palavras-chave com a IA do usuário.
 *
 * A chave é **do usuário** e fica guardada no servidor (nunca volta para a tela
 * inteira). Aqui a pessoa configura o provedor (DeepSeek, OpenAI, Ollama ou outro
 * compatível), escolhe o idioma e traduz o resumo **com as palavras-chave** — o
 * abstract sem as keywords traduzidas é meia tradução. Para o inglês o resultado
 * entra direto no `abstract`/`keywords`; para o espanhol (e qualquer outro idioma)
 * vira uma seção nova em `resumos`.
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Languages, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { api, type ConfiguracaoIA } from '@/lib/api';
import type { DocumentoAcademico } from '@miolos/core';

type IdiomaPronto = 'ingles' | 'espanhol' | 'outro';

const ROTULO_PROVEDOR: Record<ConfiguracaoIA['provedor'], string> = {
  DEEPSEEK: 'DeepSeek',
  OPENAI: 'OpenAI',
  OLLAMA: 'Ollama (nuvem)',
  OUTRO: 'Outra IA (URL própria)',
};

interface Props {
  documento: DocumentoAcademico;
  aoEditar: (mudanca: (atual: DocumentoAcademico) => DocumentoAcademico) => void;
}

export function TradutorIa({ documento, aoEditar }: Props) {
  const [config, setConfig] = useState<ConfiguracaoIA | null>(null);
  const [idioma, setIdioma] = useState<IdiomaPronto>('ingles');
  /** O nome do idioma quando é "outro" — ex.: "frances", "alemao". */
  const [idiomaNome, setIdiomaNome] = useState('');
  /** O rótulo da seção quando é "outro" — ex.: "Résumé", "Zusammenfassung". */
  const [tituloDoResumo, setTituloDoResumo] = useState('');
  const [alvo, setAlvo] = useState<'resumo' | 'palavras'>('resumo');
  const [resultado, setResultado] = useState('');
  /**
   * A tradução das palavras-chave, quando o alvo é o resumo.
   *
   * Vai junto de propósito: um abstract em inglês com palavras-chave em
   * português embaixo é o tipo de coisa que a banca vê na primeira página.
   */
  const [resultadoPalavras, setResultadoPalavras] = useState('');
  const [trabalhando, setTrabalhando] = useState(false);
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState(false);

  const recarregar = useCallback(async () => {
    try {
      setConfig(await api.configuracaoIA());
    } catch {
      setConfig(null);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const idiomaParaEnviar = idioma === 'outro' ? idiomaNome.trim() : idioma;

  const traduzir = async () => {
    const texto =
      alvo === 'resumo'
        ? documento.resumo.trim()
        : documento.palavrasChave.join(', ');
    if (!texto) {
      setErro(alvo === 'resumo' ? 'Escreva o resumo antes de traduzir.' : 'Escreva as palavras-chave antes de traduzir.');
      return;
    }
    if (idioma === 'outro' && !idiomaNome.trim()) {
      setErro('Escreva o nome do idioma (ex.: frances, alemao).');
      return;
    }
    setTrabalhando(true);
    setErro('');
    try {
      const { traducao } = await api.traduzirTexto({ texto, idioma: idiomaParaEnviar });
      setResultado(traducao);

      /*
       * Traduzir o resumo traduz as palavras-chave junto.
       *
       * São duas chamadas porque a rota traduz **um texto** — e uma lista de
       * palavras-chave não é texto corrido: mandar tudo numa frase só faria a IA
       * "explicar" em vez de traduzir. A segunda chamada é barata (cinco ou seis
       * palavras), e o resultado aparece num campo próprio, editável.
       */
      if (alvo === 'resumo' && documento.palavrasChave.length > 0) {
        const palavras = documento.palavrasChave.join('; ');
        try {
          const resposta = await api.traduzirTexto({ texto: palavras, idioma: idiomaParaEnviar });
          setResultadoPalavras(resposta.traducao);
        } catch {
          /* O resumo já está traduzido; as palavras a pessoa ajusta à mão. */
          setResultadoPalavras('');
          setErro('O resumo foi traduzido, mas as palavras-chave não. Tente traduzir só elas em “O que traduzir”.');
        }
      } else {
        setResultadoPalavras('');
      }
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível traduzir agora.');
    } finally {
      setTrabalhando(false);
    }
  };

  /** As palavras-chave traduzidas, separadas por vírgula, ponto e vírgula ou linha. */
  const palavrasTraduzidas = resultadoPalavras
    .split(/[;,\n]/)
    .map((palavra) => palavra.trim().replace(/\.$/, ''))
    .filter(Boolean);

  const aplicar = () => {
    if (!resultado.trim()) return;
    const palavras = resultado.split(/[;,]/).map((p) => p.trim()).filter(Boolean);

    if (idioma === 'ingles') {
      /* O inglês tem campo próprio: abstract/keywords. */
      if (alvo === 'resumo') {
        aoEditar((atual) => ({
          ...atual,
          abstract: resultado,
          /* Sem palavras-chave traduzidas, o que já estava salvo continua. */
          ...(palavrasTraduzidas.length > 0 ? { keywords: palavrasTraduzidas } : {}),
        }));
      } else {
        aoEditar((atual) => ({ ...atual, keywords: palavras }));
      }
    } else {
      /* Espanhol e os demais viram uma seção nova, com o rótulo do idioma. */
      const titulo = idioma === 'espanhol' ? 'Resumen' : tituloDoResumo.trim() || idiomaNome.trim() || 'Resumo';
      aoEditar((atual) => ({
        ...atual,
        resumos: [
          ...(atual.resumos ?? []),
          {
            titulo,
            resumo: alvo === 'resumo' ? resultado : '',
            palavrasChave:
              alvo === 'resumo'
                ? palavrasTraduzidas
                : palavras,
          },
        ],
      }));
    }
    setResultado('');
    setResultadoPalavras('');
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(resultado);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      setErro('Não foi possível copiar. Selecione e copie à mão.');
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Languages className="size-4" />
          Traduzir com IA
        </p>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="contorno" tamanho="pequeno">
              <Settings2 />
              {config?.configurada ? 'Configurar IA' : 'Configurar IA'}
            </Button>
          </DialogTrigger>
          <DialogContent titulo="IA do usuário" descricao="A chave é sua e fica guardada só para você. Ela nunca volta para a tela.">
            <FormularioDeConfiguracao aoSalvar={recarregar} config={config} />
          </DialogContent>
        </Dialog>
      </div>

      {!config?.configurada ? (
        <p className="text-xs leading-relaxed texto-suave">
          Configure a chave da sua IA (DeepSeek, OpenAI ou outra) para traduzir o
          resumo e as palavras-chave para inglês, espanhol ou outro idioma.
        </p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <Campo rotulo="O que traduzir" dica="Traduzir o resumo traz as palavras-chave junto.">
              <select
                value={alvo}
                onChange={(evento) => setAlvo(evento.target.value as 'resumo' | 'palavras')}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="resumo">Resumo e palavras-chave</option>
                <option value="palavras">Só as palavras-chave</option>
              </select>
            </Campo>
            <Campo rotulo="Para">
              <select
                value={idioma}
                onChange={(evento) => setIdioma(evento.target.value as IdiomaPronto)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="ingles">Inglês</option>
                <option value="espanhol">Espanhol</option>
                <option value="outro">Outro idioma...</option>
              </select>
            </Campo>
          </div>

          {idioma === 'outro' ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Campo rotulo="Idioma" dica="O nome, por extenso: frances, alemao, italiano...">
                <input
                  value={idiomaNome}
                  onChange={(evento) => setIdiomaNome(evento.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  placeholder="frances"
                />
              </Campo>
              <Campo rotulo="Título da seção" dica="Como sai impresso: Résumé, Zusammenfassung...">
                <input
                  value={tituloDoResumo}
                  onChange={(evento) => setTituloDoResumo(evento.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  placeholder="Résumé"
                />
              </Campo>
            </div>
          ) : null}

          <Button tamanho="pequeno" onClick={() => void traduzir()} disabled={trabalhando}>
            {trabalhando ? <Loader2 className="animate-spin" /> : <Languages />}
            Traduzir
          </Button>

          {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

          {resultado ? (
            <div className="space-y-2">
              <Campo rotulo={alvo === 'resumo' ? 'Resumo traduzido' : 'Palavras-chave traduzidas'}>
                <textarea
                  value={resultado}
                  onChange={(evento) => setResultado(evento.target.value)}
                  rows={5}
                  className="w-full rounded-md border border-input bg-background p-3 text-sm"
                />
              </Campo>

              {alvo === 'resumo' && resultadoPalavras ? (
                <Campo
                  rotulo="Palavras-chave traduzidas"
                  dica="Separe com ponto e vírgula. Elas entram junto com o resumo."
                >
                  <textarea
                    value={resultadoPalavras}
                    onChange={(evento) => setResultadoPalavras(evento.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-input bg-background p-3 text-sm"
                  />
                </Campo>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button tamanho="pequeno" onClick={aplicar}>
                  <Check />
                  {idioma === 'ingles'
                    ? alvo === 'resumo'
                      ? `Aplicar ao abstract${palavrasTraduzidas.length > 0 ? ' e às keywords' : ''}`
                      : 'Aplicar às keywords'
                    : alvo === 'resumo'
                      ? 'Adicionar resumo e palavras-chave ao documento'
                      : 'Adicionar palavras-chave ao documento'}
                </Button>
                <Button variant="contorno" tamanho="pequeno" onClick={() => void copiar()}>
                  {copiado ? <Check /> : <Copy />}
                  {copiado ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
              {idioma !== 'ingles' ? (
                <p className="text-xs texto-suave">
                  Entra como uma seção nova de resumo, com o rótulo que você escolheu — e continua
                  editável em <strong>Resumos em outros idiomas</strong>.
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function FormularioDeConfiguracao({
  config,
  aoSalvar,
}: {
  config: ConfiguracaoIA | null;
  aoSalvar: () => Promise<void>;
}) {
  const [provedor, setProvedor] = useState<ConfiguracaoIA['provedor']>(config?.provedor ?? 'DEEPSEEK');
  const [modelo, setModelo] = useState(config?.modelo ?? 'deepseek-chat');
  const [urlBase, setUrlBase] = useState(config?.urlBase ?? '');
  const [apiKey, setApiKey] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvo, setSalvo] = useState(false);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      await api.salvarConfiguracaoIA({
        apiKey: apiKey || undefined,
        provedor,
        modelo: modelo.trim() || undefined,
        urlBase: provedor === 'OUTRO' ? urlBase.trim() : undefined,
      });
      setSalvo(true);
      setApiKey('');
      await aoSalvar();
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4">
      <Campo rotulo="Provedor">
        <select
          value={provedor}
          onChange={(evento) => setProvedor(evento.target.value as ConfiguracaoIA['provedor'])}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="DEEPSEEK">DeepSeek</option>
          <option value="OPENAI">OpenAI</option>
          <option value="OLLAMA">Ollama (nuvem)</option>
          <option value="OUTRO">Outra IA (URL própria)</option>
        </select>
      </Campo>
      <Campo rotulo="Modelo" dica="Ex.: deepseek-chat, gpt-4o-mini.">
        <input
          value={modelo}
          onChange={(evento) => setModelo(evento.target.value)}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          placeholder={provedor === 'DEEPSEEK' ? 'deepseek-chat' : 'gpt-4o-mini'}
        />
      </Campo>
      {provedor === 'OUTRO' ? (
        <Campo rotulo="URL base" dica="O endereço do provedor, no formato do OpenAI (ex.: https://api.exemplo.com/v1).">
          <input
            value={urlBase}
            onChange={(evento) => setUrlBase(evento.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            placeholder="https://api.exemplo.com/v1"
          />
        </Campo>
      ) : null}
      <Campo
        rotulo="Chave da API"
        dica={config?.configurada ? 'Deixe em branco para manter a chave atual.' : 'A chave fica guardada só para a sua conta.'}
      >
        <input
          type="password"
          value={apiKey}
          onChange={(evento) => setApiKey(evento.target.value)}
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          placeholder="sk-..."
        />
      </Campo>
      {erro ? <p className="text-xs text-destructive">{erro}</p> : null}
      <Button tamanho="pequeno" onClick={() => void salvar()} disabled={salvando}>
        {salvando ? <Loader2 className="animate-spin" /> : <Check />}
        {salvo ? 'Salvo' : 'Salvar configuração'}
      </Button>
    </div>
  );
}
